<template>
  <div class="hsn-rates-view">
    <n-page-header title="HSN Rate Catalog" subtitle="Manage GST rates for the product type catalog used across all businesses">
      <template #extra>
        <n-space>
          <n-button :loading="isSyncing" @click="handleSync">
            Refresh GST Rates
          </n-button>
        </n-space>
      </template>
    </n-page-header>

    <!-- Sync Result -->
    <n-alert v-if="syncResult" :type="syncResult.type" closable style="margin-bottom: 16px" @close="syncResult = null">
      {{ syncResult.message }}
    </n-alert>

    <!-- Stats -->
    <n-grid :cols="4" :x-gap="16" :y-gap="16" style="margin-bottom: 16px">
      <n-gi>
        <n-card>
          <n-statistic label="Total HSN Codes" :value="rates.length" />
        </n-card>
      </n-gi>
      <n-gi>
        <n-card>
          <n-statistic label="Nil (0%)" :value="rates.filter(r => r.gst_rate === 0).length" />
        </n-card>
      </n-gi>
      <n-gi>
        <n-card>
          <n-statistic label="5% Slab" :value="rates.filter(r => r.gst_rate === 5).length" />
        </n-card>
      </n-gi>
      <n-gi>
        <n-card>
          <n-statistic label="18% Slab" :value="rates.filter(r => r.gst_rate === 18).length" />
        </n-card>
      </n-gi>
    </n-grid>

    <!-- Rates Table -->
    <n-card title="All HSN Codes" style="margin-bottom: 16px">
      <template #header-extra>
        <n-input v-model:value="searchQuery" placeholder="Search HSN or product..." clearable style="width: 300px" />
      </template>
      <n-data-table
        :columns="columns"
        :data="filteredRates"
        :loading="isLoading"
        :pagination="{ pageSize: 50 }"
        size="small"
        striped
      />
    </n-card>

    <!-- Changelog -->
    <n-card title="Rate Change History">
      <n-data-table
        :columns="changelogColumns"
        :data="changelog"
        :loading="isLoadingChangelog"
        :pagination="{ pageSize: 20 }"
        size="small"
      />
      <n-empty v-if="!isLoadingChangelog && changelog.length === 0" description="No rate changes recorded yet" />
    </n-card>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, h } from 'vue'
import { NTag } from 'naive-ui'
import { useToast } from '@shared/composables/useToast.js'
import { apiClient } from '@shared/utils/apiClient.js'

const toast = useToast()

const rates = ref([])
const changelog = ref([])
const isLoading = ref(false)
const isLoadingChangelog = ref(false)
const isSyncing = ref(false)
const syncResult = ref(null)
const searchQuery = ref('')

const filteredRates = computed(() => {
  if (!searchQuery.value) return rates.value
  const q = searchQuery.value.toLowerCase()
  return rates.value.filter(r =>
    r.hsn.includes(q) || r.label.toLowerCase().includes(q)
  )
})

const rateTag = (rate) => {
  if (rate === 0) return h(NTag, { type: 'success', size: 'small' }, () => 'Nil')
  if (rate === 5) return h(NTag, { type: 'info', size: 'small' }, () => '5%')
  if (rate === 18) return h(NTag, { type: 'warning', size: 'small' }, () => '18%')
  if (rate === 40) return h(NTag, { type: 'error', size: 'small' }, () => '40%')
  return h(NTag, { size: 'small' }, () => `${rate}%`)
}

const columns = [
  { title: 'HSN', key: 'hsn', width: 100, sorter: 'default' },
  { title: 'Product Type', key: 'label', width: 300, sorter: 'default' },
  { title: 'GST Rate', key: 'gst_rate', width: 100, sorter: 'default', render: (row) => rateTag(row.gst_rate) },
  { title: 'Chapter', key: 'chapter', width: 80, sorter: 'default' },
]

const changelogColumns = [
  { title: 'HSN', key: 'hsn', width: 80 },
  { title: 'Product', key: 'label', width: 250 },
  { title: 'Old Rate', key: 'old_rate', width: 90, render: (row) => `${row.old_rate}%` },
  { title: 'New Rate', key: 'new_rate', width: 90, render: (row) => rateTag(row.new_rate) },
  { title: 'Source', key: 'source', width: 130 },
  { title: 'Effective Date', key: 'effective_date', width: 130 },
  { title: 'Synced At', key: 'synced_at', width: 160, render: (row) => new Date(row.synced_at).toLocaleString('en-IN') },
]

const fetchRates = async () => {
  isLoading.value = true
  try {
    const res = await apiClient.get('/v1/admin/hsn-rates')
    rates.value = res.rates || res.data?.rates || []
  } catch (error) {
    console.error('Failed to fetch HSN rates:', error)
    toast.error('Failed to load HSN rates')
  } finally {
    isLoading.value = false
  }
}

const fetchChangelog = async () => {
  isLoadingChangelog.value = true
  try {
    const res = await apiClient.get('/v1/admin/hsn-rates/changelog?limit=100')
    changelog.value = res.entries || res.data?.entries || []
  } catch {
    // Silent — table may not exist yet
  } finally {
    isLoadingChangelog.value = false
  }
}

const handleSync = async () => {
  isSyncing.value = true
  syncResult.value = null
  try {
    // Fetch current rates to show count
    await fetchRates()
    syncResult.value = {
      type: 'success',
      message: `HSN catalog loaded: ${rates.value.length} entries. To sync from external source, deploy and trigger the Cloudflare Worker.`,
    }
    // Also refresh changelog
    await fetchChangelog()
  } catch (error) {
    syncResult.value = {
      type: 'error',
      message: 'Sync failed: ' + (error.message || 'Unknown error'),
    }
  } finally {
    isSyncing.value = false
  }
}

onMounted(() => {
  fetchRates()
  fetchChangelog()
})
</script>

<style scoped>
.hsn-rates-view {
  padding: 16px;
}
</style>
